import { QuestionCircleOutlined } from '@ant-design/icons'
import { ConditionalWrapper } from '@supplement-bacon/react-toolkit/utils'
import { Card, Empty, Flex, Spin, Tooltip, Typography } from 'antd'
import { ReactNode } from 'react'
import { ResponsiveContainer } from 'recharts'

const { Title } = Typography

interface BaseChartProps<T> {
  title?: string
  help?: string
  data: T[] | T | undefined
  isLoading: boolean
  error?: string
  height?: number
  actions?: ReactNode
  children: ReactNode
  isEmpty?: (data: T[] | T | undefined) => boolean
  displayOnlyChart?: boolean
}

function BaseChart<T>({
  title,
  help,
  data,
  isLoading,
  error,
  height = 383,
  actions,
  children,
  isEmpty = (data) => !data || (Array.isArray(data) && data.length === 0),
  displayOnlyChart = false,
}: BaseChartProps<T>) {
  const isDataEmpty = isEmpty(data)

  return (
    <ConditionalWrapper
      condition={!displayOnlyChart}
      wrapper={(children) => (
        <Card loading={isLoading}>
          {title && (
            <Flex justify="space-between" style={{ marginBottom: 16 }}>
              <Title level={5} className="m-0">
                {title}{' '}
                {help && (
                  <Tooltip title={help}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                )}
              </Title>
              {actions}
            </Flex>
          )}
          {children}
        </Card>
      )}
    >
      <>
        {isLoading && <Spin />}
        {(isDataEmpty || error) && !isLoading && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
        )}
        {!isDataEmpty && !isLoading && !error && (
          <ResponsiveContainer width="100%" height={height}>
            {children}
          </ResponsiveContainer>
        )}
      </>
    </ConditionalWrapper>
  )
}

export default BaseChart
